import { action, batch } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { getCachedDelugeId } from '@root/entities/auth'
import { authStateAtom } from '@root/entities/auth/atoms'
import { User } from '@root/entities/auth/model'
import { AuthRoute } from '@root/features/routing'
import { redirectToLastVisitedNonAuthRouteAction } from '@root/features/routing/atoms'
import {
  initializeSecureStorageStateAction,
  storageSessionStateAtom,
} from '@root/features/secure-storage/atoms'
import { StorageSession } from '@root/features/secure-storage/model'
import {
  getSessionFromWorkerAction,
  getUserFromWorkerAction,
} from '@root/features/shared-worker/atoms'
import {
  isBrowserLocalAtom,
  isTauriOrBrowserLocalAtom,
} from '@root/shared/core'
import { routerActions } from '@root/shared/lib/react-router'

import { InitializationState } from '../../model'
import {
  authStateReaction,
  initializationStateAtom,
  initializeInitializationDeepLinksAction,
  meRequestStateReaction,
  secureStorageStateReaction,
} from '../common'
import { initializeUserAction } from '../common/reatom-me'

const tryInitializedFromWorkerCache = action(async (ctx) => {
  performance.mark('tryInitializedFromWorkerCacheStart', {
    detail: {
      devTools: {
        dataType: 'marker',
      },
    },
  })
  const [cachedUserResult, cachedSessionResult] = await ctx.schedule(() =>
    Promise.all([
      ctx.schedule(() => getUserFromWorkerAction(ctx, {})),
      ctx.schedule(() => getSessionFromWorkerAction(ctx, {})),
    ]),
  )
  performance.mark('tryInitializedFromWorkerCacheEnd', {
    detail: {
      devTools: {
        dataType: 'marker',
      },
    },
  })
  const cachedUser = Result.getOrUndefined(cachedUserResult)?.user
  const cachedSession = Result.getOrUndefined(cachedSessionResult)?.session

  if (!cachedUser || !cachedSession) {
    return
  }

  const session = StorageSession.fromDto(cachedSession)
  if (!(session instanceof StorageSession.Unlocked)) {
    return
  }
  const user = User.decode(cachedUser)
  if (!user) {
    return
  }
  authStateAtom.updateUserOrSetLoggedIn(ctx, user)
  storageSessionStateAtom.setState(ctx, AsyncStateN.success(session))
}, 'tryInitializedFromWorkerCache')

export const initializeInitializationAction = action(async (ctx) => {
  if (!ctx.get(isTauriOrBrowserLocalAtom)) {
    initializationStateAtom(ctx, new InitializationState.Website())
    /**
     * Ask who is signed in, which the desktop path does at the bottom of this
     * action and the web path used to skip.
     *
     * On the web nothing fetched `/me` except signing in, so a reload of an
     * existing session left `userAtom` empty for the rest of the visit. That
     * atom is what the sidebar reads to draw the account link, so the profile
     * had no way in at all — it could only be reached by typing the URL — and
     * every screen derived from the user rendered as signed out while the app
     * was, in every other respect, signed in.
     *
     * The reaction that redirects to the sign-in page on a 401 is deliberately
     * not wired here: an anonymous visitor is allowed to look at a pair, and
     * this request failing just leaves the sidebar showing Login, as before.
     */
    initializeUserAction(ctx)
    return
  }

  initializeInitializationDeepLinksAction(ctx)

  authStateReaction(ctx)
  meRequestStateReaction(ctx)
  secureStorageStateReaction(ctx, (ctx) => {
    redirectToLastVisitedNonAuthRouteAction(ctx)
  })
  if (ctx.get(isBrowserLocalAtom)) {
    await ctx.schedule(() => tryInitializedFromWorkerCache(ctx))
  }

  const state = ctx.get(initializationStateAtom)
  if (
    state instanceof InitializationState.Website ||
    state instanceof InitializationState.Ready
  ) {
    return
  }

  batch(ctx, () => {
    routerActions.navigate(ctx, AuthRoute.build())
    const cachedDelugeId = getCachedDelugeId()
    if (cachedDelugeId) {
      initializeSecureStorageStateAction(ctx, cachedDelugeId)
    }

    initializeUserAction(ctx)
  })
}, 'initializeInitializationAction')

export * from '../common/reatom-me'
